import { useContext, useEffect, useState } from "react";
import { Pledge, PoolStatus } from "../../interfaces";
import AuthContext from "../../store/Auth/AuthContext";
import SubscriptionContext from "../../store/Subscription/SubscriptionContext";
import WalletContext from "../../store/Wallet/WalletContext";
import { appConfig } from "../../config";

enum ButtonText {
    GET_WHITELISTED = 'Get Whitelisted',
    LOGIN = 'Login',
    CLOSED = 'Learn More',
    DETAILS = 'View Details',
    PORTFOLIO = 'View Portfolio',
    SOON = 'Coming Soon',
    SOON_DATE = 'Launching Soon',
    SOON_DATE_CLOSES = 'Whitelist Closes',
    WHITELIST_CLOSED = 'Whitelist Closed',
    WHITELIST_CLOSES = 'Invest Soon',
    FAILED = 'Failed',
    AWAITING = 'Successfully Applied, Awaiting Results',
    CONTRIBUTE = 'Invest Now',
    UNLUCKY = 'Unlucky',
    INVEST = 'Invest More',
    INVESTED = 'Already Invested',
    REQUIRES_STAKING = 'Stake GAINS',
    STAKE_ADDITIONAL = 'Stake Additional GAINS',
    STAKE_MORE = 'Stake More GAINS',
    WHITELIST_EXPIRED = 'Whitelist Expired',
    REQUIRES_KYC = 'Finish KYC'
}

interface ButtonState {
    buttonText: ButtonText | null,
    disabled: boolean,
}

interface PoolButtonHookProps {
    poolId: string,
    poolStatus: PoolStatus,
    quizStatus?: boolean,
    whitelistedData?: Pledge | null,
    whitelisted?: boolean,
    kycDeadlineDate?: Date,
    invest?: any,
    deadlineDate?: Date | null,
    dealPage?: boolean,
    showTimer?: boolean,
    isGiveaway?: boolean,
    userTier?: number,
    isExternalProject?: boolean,
}

// TODO: Add pool type to interface
// quizStatus is actually quiz failed
const usePoolButton = (props: PoolButtonHookProps): ButtonState => {

    const { isAuth, getProfileData } = useContext<any>(AuthContext);
    const { hasKycWallet } = useContext<any>(WalletContext);
    const { lockedGains } = useContext<any>(SubscriptionContext);

    const [buttonText, setButtonText] = useState<ButtonText | null>(null);
    const [disabled, setDisabled] = useState(false);

    const giveAwayTiers = appConfig.giveAwayUserTiers;
    const canParticipateInGiveaway = props.userTier && giveAwayTiers.includes(props.userTier);

    useEffect(() => {
        if (props.poolStatus === 'closed') {
            setButtonText(ButtonText.CLOSED)
            setDisabled(false)
        }

        if (props.poolStatus === 'upcoming') {
            if (props.deadlineDate && isAuth) {
                setButtonText(ButtonText.SOON_DATE)
                if (props.dealPage) {
                    setDisabled(false)
                } else setDisabled(true)
            } else {
                setDisabled(true)
                setButtonText(ButtonText.SOON) 
            }
        }

        if (props.poolStatus === 'whitelist_closed') {
            if (props.deadlineDate && isAuth) {
                setButtonText(ButtonText.WHITELIST_CLOSES)
                setDisabled(false)
            }
            else {
                setButtonText(ButtonText.WHITELIST_CLOSED)
                setDisabled(true)
            }
        }

        if (props.poolStatus === 'whitelist_open') {
            if (!isAuth) {
                setButtonText(ButtonText.GET_WHITELISTED)
                setDisabled(true)
            } else {
                if (props.quizStatus) {
                    setButtonText(ButtonText.FAILED)
                    setDisabled(true)
                } else if (props.whitelistedData) {
                    setButtonText(ButtonText.AWAITING)
                    setDisabled(true)
                } else {
                    if (props.dealPage) {
                        setButtonText(ButtonText.GET_WHITELISTED)
                    } else if (props.deadlineDate && new Date(props.deadlineDate) < new Date()) setButtonText(ButtonText.WHITELIST_EXPIRED)
                    else if (props.isExternalProject) {
                        if (getProfileData.kycStatus !== 'approved' || !hasKycWallet) setButtonText(ButtonText.REQUIRES_KYC)
                        else setButtonText(ButtonText.GET_WHITELISTED)
                    }
                    else if (lockedGains === 0) setButtonText(ButtonText.REQUIRES_STAKING)
                    else if (lockedGains < 1000) setButtonText(ButtonText.STAKE_MORE)
                    else if (props.isGiveaway && !canParticipateInGiveaway && getProfileData.kycStatus == 'approved' && hasKycWallet) setButtonText(ButtonText.STAKE_ADDITIONAL)
                    else if (props.deadlineDate && props.showTimer) setButtonText(ButtonText.GET_WHITELISTED)
                    else setButtonText(ButtonText.GET_WHITELISTED)
                    if (props.dealPage) {
                        setDisabled(false)
                    } else {
                        if (props.deadlineDate && new Date(props.deadlineDate) < new Date()) setDisabled(true)
                        else if (lockedGains < 1000 && !props.isExternalProject ) {
                            setDisabled(false)
                        } else if ( (!getProfileData.kycStatus || getProfileData.kycStatus !== 'approved' || !hasKycWallet) && !props.isExternalProject) {
                            setDisabled(true)
                        } else if (props.isExternalProject && getProfileData.kycStatus === 'pending') {
                            setDisabled(true)
                        } else {
                            setDisabled(false)
                        }
                    }
                }
            }
        }

        if (props.poolStatus === 'ongoing') {
            if (!isAuth) {
                setButtonText(ButtonText.CONTRIBUTE)
                setDisabled(true)
            } else {
                if (props.invest && props.invest.whitelist && props.invest.whitelist > 0 && getProfileData?.kycStatus === 'approved' ) {
                    setButtonText(ButtonText.CONTRIBUTE)
                    setDisabled(false)
                } else {
                    setButtonText(ButtonText.UNLUCKY)
                    setDisabled(true)
                }
            }
        }

    }, [props, isAuth])

    return {
        buttonText,
        disabled,
    }
}

export default usePoolButton;